import React, { useState } from "react";
import { Loader2, SearchX, AlertTriangle, RefreshCw, FileDown } from "lucide-react";
import { useAiSearch } from "./useSearch";
import { AiAnswer } from "./AiAnswer";
import { EntityHead, FactCards, OtherMatches, DataUsed } from "./SearchMeta";
import { RecentSearches } from "./RecentSearches";
import { FollowUps } from "./FollowUps";
import { downloadBrief } from "../../lib/searchApi";

const BTN = "inline-flex items-center gap-1.5 px-2 py-1 border border-border rounded-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50";

function Note({ icon: Icon, testid, tone = "text-muted", children }) {
  return (
    <div data-testid={testid} className={`flex items-start gap-2.5 text-[13px] ${tone} border border-border bg-surface rounded-sm px-4 py-3.5`}>
      <Icon size={15} className="shrink-0 mt-0.5" /><div>{children}</div>
    </div>
  );
}

function PdfButton({ id }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const go = () => { setBusy(true); setErr(null); downloadBrief(id).catch(e => setErr(e.message)).finally(() => setBusy(false)); };
  return (
    <>
      <button type="button" data-testid="ai-search-pdf" onClick={go} disabled={busy} className={BTN}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} Download PDF brief
      </button>
      {err && <span data-testid="ai-search-pdf-error" className="text-critical">{err}</span>}
    </>
  );
}

function Footer({ s, req, onSearch }) {
  if (s.status !== "done" || !s.done) return null;
  const when = s.done.created_at ? new Date(s.done.created_at).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }) : "";
  const e = s.meta?.entity;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted border-t border-border pt-3">
      <span data-testid="ai-search-answer-meta">{s.meta?.model || "Claude Opus 5.5"} · {s.done.cached ? `saved answer from ${when}` : `answered ${when}`}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {s.done.id && <PdfButton id={s.done.id} />}
        {e && (
          <button type="button" data-testid="ai-search-refresh" onClick={() => onSearch({ q: s.meta.q || req.q, kind: e.kind, code: e.code, fresh: true })} className={BTN}>
            <RefreshCw size={11} /> Fresh answer
          </button>
        )}
      </div>
    </div>
  );
}

export default function AiSearchBody({ req, onSearch }) {
  const s = useAiSearch(req);
  if (s.status === "idle") return <RecentSearches onSearch={onSearch} />;
  if (s.status === "nomatch") return (
    <Note icon={SearchX} testid="ai-search-nomatch">
      No SKU, branch or supplier in the cleaned Silver / Gold tables matches “{s.meta?.q || req.q}”. Try a full or partial SKU code,
      a branch code, name or city, or a supplier code or name.
    </Note>
  );
  const f = s.meta?.facts;
  return (
    <div className="space-y-5" data-testid="ai-search-result">
      {!s.meta && <Note icon={Loader2} testid="ai-search-loading"><span className="inline-flex items-center gap-2">Reading the Silver and Gold tables…</span></Note>}
      {s.meta?.entity && <EntityHead entity={s.meta.entity} name={s.meta.name} facts={f} />}
      {f && <FactCards cards={f.cards} />}
      {s.meta?.entity && <AiAnswer text={s.text} streaming={s.status === "streaming" || s.status === "loading"} />}
      {s.status === "error" && <Note icon={AlertTriangle} testid="ai-search-error" tone="text-critical">{s.error}</Note>}
      <Footer s={s} req={req} onSearch={onSearch} />
      {s.status === "done" && s.done?.id && <FollowUps key={s.done.id} id={s.done.id} initial={s.done.turns} />}
      {s.meta?.matches?.length > 1 && <OtherMatches matches={s.meta.matches.slice(1)} q={s.meta.q} onSearch={onSearch} />}
      {f && <DataUsed facts={f} />}
    </div>
  );
}
